// Demo-mode branches use baked pools from data/demo-data.ts.
import { useMemo, useState } from 'react';
import { SectionLabel } from '../components/primitives.js';
import { type CanopusStaffStatus } from '../../../services/canopusCommand.js';
// Demo staff overlay (status fields on the baked pool).
import { applyDemoStaffOverlay, getDemoSITfiRows } from '../data/demo-data.js';
import './staff.css';
// Reuse the SI × TFI Correlation table's layer-pill styles so the Layers
// column in the Staff metrics table reads the same across views.
import './si-tfi.css';

// TFI thresholds → semantic color (>=75 green, >=50 cyan, >=25 amber,
// else deep amber/orange).
function tfiColor(tfi: number): string {
  if (tfi >= 75) return '#10B981';
  if (tfi >= 50) return '#06B6D4';
  if (tfi >= 25) return '#F59E0B';
  return '#FFB300';
}

function formatDisplayName(email: string | null): string | null {
  if (!email) return null;
  const local = email.split('@')[0];
  if (!local) return null;
  return local
    .replace(/[._]/g, ' ')
    .replace(/\d+$/g, '')
    .trim()
    .split(' ')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}

// Force a capital first letter on every word of a real-person name without
// disturbing the rest of the casing. Handles the common case where a staff
// member's profile name comes in fully lowercase ("john smith") while
// leaving intentional mixed-case names ("McDonald", "deLuca") untouched.
// Empty / nullish input returns null so callers can chain fallbacks.
export function capitalizeName(name: string | null | undefined): string | null {
  if (!name) return null;
  const trimmed = name.trim();
  if (!trimmed) return null;
  return trimmed
    .split(/\s+/)
    .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1) : w))
    .join(' ');
}

interface StaffDisplayRow {
  n: string;
  email: string | null;
  on: boolean;
  wasEarlier: boolean;
  o: string;
  r: string;
  /** Most recent procedure the staff member participated in; '—' when unknown. */
  proc: string;
  d: string;
  tfi: number | null;
  tfiL1: number | undefined;
  tfiL2: number | undefined;
  tfiL3: number | undefined;
  siToday: number;
  siOffCase: number;
  /** Demo-only: expected SI count given TFI / role. null when no benchmark exists yet. */
  expected: number | null;
  /** Demo-only: SI events still needed before a benchmark can be computed. null when active. */
  remainingForBenchmark: number | null;
}

// Partition staff by today's activity for the three-section layout.
// - active: currently active with eXeX in a room
// - earlier: not active now, but appeared in any of today's PR participants
//   OR submitted at least one SI today
// - inactive: no signal of activity today
export function partitionStaff(rows: StaffDisplayRow[]): {
  active: StaffDisplayRow[];
  earlier: StaffDisplayRow[];
  inactive: StaffDisplayRow[];
} {
  const active: StaffDisplayRow[] = [];
  const earlier: StaffDisplayRow[] = [];
  const inactive: StaffDisplayRow[] = [];
  for (const s of rows) {
    if (s.on) active.push(s);
    else if (s.wasEarlier || s.siToday > 0) earlier.push(s);
    else inactive.push(s);
  }
  return { active, earlier, inactive };
}

// ─── StaffTab ─ Staff tab ─────────────────────────────────────────
export function StaffTab({
  facilityId: _facilityId,
  roomLabel: _roomLabel = 'OR',
  // when true, apply the demo overlay to the real staff list.
  demoMode = false,
  // hoisted real staff fetch from the shell.
  realStaff,
}: {
  facilityId: string | null;
  roomLabel?: string;
  demoMode?: boolean;
  realStaff: CanopusStaffStatus | null;
}) {
  const [staffSearch, setStaffSearch] = useState('');
  const [positionFilter, setPositionFilter] = useState('all');
  // The "Not Active" bucket is long and rarely the focus of attention —
  // collapse it by default so the Active / Active Earlier sections stay
  // above the fold. Click the header to expand.
  const [inactiveCollapsed, setInactiveCollapsed] = useState(true);

  // when demoMode is on, apply the overlay to whatever real
  // staff the shell fetched; otherwise use the real data as-is. Memoized
  // because applyDemoStaffOverlay generates a fresh timestamp on every call.
  const live: CanopusStaffStatus | null = useMemo(() => {
    if (!realStaff) return null;
    return demoMode ? applyDemoStaffOverlay(realStaff) : realStaff;
  }, [realStaff, demoMode]);
  const isLoading = realStaff === null;

  // demo TFI rows are keyed by display name so we can merge
  // them onto each member row below without depending on userId. Real mode
  // returns an empty map so TFI columns render as '--'.
  const demoTfiByName = useMemo(() => {
    if (!demoMode) return new Map<string, ReturnType<typeof getDemoSITfiRows>[number]>();
    const rows = getDemoSITfiRows(realStaff?.members ?? null);
    const map = new Map<string, (typeof rows)[number]>();
    rows.forEach((r) => map.set(r.name, r));
    return map;
  }, [demoMode, realStaff]);

  const displayRows: StaffDisplayRow[] = live
    ? live.members.map((m) => {
        const tfiRow = demoTfiByName.get(m.name ?? '');
        return {
          n: capitalizeName(m.name) || formatDisplayName(m.email) || m.userId,
          email: m.email,
          on: m.onDuty,
          wasEarlier: m.wasActiveEarlierToday,
          o: m.currentRoom ?? m.lastRoomToday ?? '—',
          r: m.position ?? '—',
          proc: m.lastProcedureName ?? '—',
          d: '--',
          tfi: tfiRow?.tfi ?? null,
          tfiL1: tfiRow?.layers[0],
          tfiL2: tfiRow?.layers[1],
          tfiL3: tfiRow?.layers[2],
          siToday: m.siToday,
          siOffCase: m.siOffCase,
          expected: tfiRow?.expected ?? null,
          remainingForBenchmark: tfiRow?.remainingForBenchmark ?? null,
        };
      })
    : [];

  // Build the position filter options from the positions actually present
  // in the data. Keeps the dropdown in sync with whatever the backend
  // surfaces from the Members `position` field.
  const availablePositions = Array.from(
    new Set(displayRows.map((s) => s.r).filter((r) => r && r !== '—'))
  ).sort();

  const filteredStaff = displayRows.filter((s) => {
    if (positionFilter !== 'all' && s.r !== positionFilter) return false;
    if (staffSearch) {
      const q = staffSearch.toLowerCase();
      const nameMatch = s.n.toLowerCase().includes(q);
      const emailMatch = s.email ? s.email.toLowerCase().includes(q) : false;
      if (!nameMatch && !emailMatch) return false;
    }
    return true;
  });

  const groups = partitionStaff(filteredStaff);

  const renderStaffGrid = (rows: StaffDisplayRow[], emptyText: string) => {
    if (rows.length === 0) {
      return <div className="staff-section-empty">{emptyText}</div>;
    }
    return (
      <div className="staff-grid">
        {rows.map((s, i) => (
          <div key={i} className="staff-card">
            <div className={`staff-dot ${s.on ? 'staff-dot--on' : 'staff-dot--off'}`} />
            <div className="staff-card-body">
              <div className="staff-name">{s.n}</div>
              <div className="staff-meta">
                {s.proc} {'•'} {s.o}
                {s.d && s.d !== '--' ? ` • ${s.d}` : ''}
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className={isLoading && !live ? 'loading-blur' : undefined}>
      <div className="staff-section staff-section--mb">
        <div className="staff-toolbar">
          <input
            type="text"
            placeholder="Search staff..."
            value={staffSearch}
            onChange={(e) => setStaffSearch(e.target.value)}
            className="staff-search"
          />
          <div className="staff-role-wrap">
            <span className="staff-role-label">Role</span>
            <select
              value={positionFilter}
              onChange={(e) => setPositionFilter(e.target.value)}
              className="staff-role-select"
            >
              <option value="all">All</option>
              {availablePositions.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          </div>
          <span className="staff-count">
            {filteredStaff.length} of {displayRows.length} staff
          </span>
        </div>
        <div className="staff-section-block">
          <SectionLabel text="Active" />
          {renderStaffGrid(groups.active, 'No active staff right now.')}
        </div>
        <div className="staff-section-block">
          <SectionLabel text="Active Earlier Today" />
          {renderStaffGrid(groups.earlier, 'No staff active earlier today.')}
        </div>
        <div className="staff-section-block">
          <button
            type="button"
            className="staff-collapse-toggle"
            aria-expanded={!inactiveCollapsed}
            onClick={() => setInactiveCollapsed((v) => !v)}
          >
            <SectionLabel text={`Not Active (${groups.inactive.length})`} />
            <span className="staff-collapse-hint">
              <span className="staff-collapse-hint-text">
                {inactiveCollapsed ? 'Show' : 'Hide'}
              </span>
              <span className="staff-collapse-chevron" aria-hidden="true">
                {inactiveCollapsed ? '▶' : '▼'}
              </span>
            </span>
          </button>
          {!inactiveCollapsed && renderStaffGrid(groups.inactive, 'No inactive staff.')}
        </div>
      </div>
      <div className="staff-section">
        <SectionLabel text="Team Familiarity & Surgical Inquiry Metrics" />
        {/* Demo mode has fabricated TFI values; real mode shows the empty-
            state message until a real TFI source is wired up. */}
        {demoMode ? (
          <table className="staff-table">
            <thead>
              <tr>
                <th className="staff-th staff-th--left">Staff</th>
                <th className="staff-th staff-th--center">TFI</th>
                <th className="staff-th staff-th--center">Layers</th>
                <th className="staff-th staff-th--right">SI Today</th>
                <th className="staff-th staff-th--right">Off-Case</th>
                <th className="staff-th staff-th--right">Expected</th>
                <th className="staff-th staff-th--center">Status</th>
              </tr>
            </thead>
            <tbody>
              {displayRows
                .filter((s) => s.on)
                .map((s, i) => {
                  const expected = s.expected;
                  const remaining = s.remainingForBenchmark;
                  const isBuilding = expected === null && remaining !== null;
                  const isActive = expected !== null;
                  const delta = isActive ? s.siToday - expected! : null;
                  const statusLabel = !isActive
                    ? isBuilding
                      ? 'Building'
                      : '--'
                    : delta! > 5
                      ? 'Above'
                      : delta! < -5
                        ? 'Below'
                        : 'Normal';
                  const statusColor = !isActive
                    ? '#6366F1'
                    : delta! > 5
                      ? '#F59E0B'
                      : delta! < -5
                        ? '#10B981'
                        : '#6366F1';
                  const statusBg = !isActive
                    ? 'rgba(99,102,241,0.15)'
                    : delta! > 5
                      ? 'rgba(245,158,11,0.15)'
                      : delta! < -5
                        ? 'rgba(16,185,129,0.15)'
                        : 'rgba(99,102,241,0.15)';
                  const tfiTextColor = s.tfi != null ? tfiColor(s.tfi) : '#556677';
                  return (
                    <tr key={i} className="staff-row">
                      <td className="staff-td-name">
                        {s.n}{' '}
                        <span className="staff-role-tag">
                          ({s.r === 'Scrub Tech' ? 'ST' : s.r === 'Circ Nurse' ? 'CN' : s.r})
                        </span>
                      </td>
                      {/* TFI color is data-driven — kept inline. */}
                      <td className="staff-td-tfi" style={{ color: tfiTextColor }}>
                        {s.tfi != null ? s.tfi : '--'}
                      </td>
                      <td className="staff-td-layers">
                        {s.tfiL1 !== undefined && s.tfiL2 !== undefined && s.tfiL3 !== undefined ? (
                          <span className="si-tfi-layers">
                            {[s.tfiL1, s.tfiL2, s.tfiL3].map((layerVal, lIdx) => {
                              const lColor = tfiColor(layerVal);
                              return (
                                <span
                                  key={lIdx}
                                  className="si-tfi-layer-pill"
                                  /* Threshold-driven layer colors — inline. */
                                  style={{
                                    background: `${lColor}22`,
                                    color: lColor,
                                    border: `1px solid ${lColor}55`,
                                  }}
                                >
                                  {layerVal}
                                </span>
                              );
                            })}
                          </span>
                        ) : (
                          '--'
                        )}
                      </td>
                      <td className="staff-td-si-today">
                        {s.siToday !== undefined ? s.siToday : '--'}
                      </td>
                      <td
                        className={`staff-td-offcase ${s.siOffCase > 0 ? 'staff-td-offcase--has' : ''}`}
                      >
                        {s.siOffCase || 0}
                      </td>
                      <td className="staff-td-expected">
                        {isActive
                          ? Math.round(expected!)
                          : isBuilding
                            ? `~${remaining} more`
                            : '--'}
                      </td>
                      <td className="staff-td-status">
                        {/* Status badge color/background is semantic per data — kept inline. */}
                        <span
                          className="staff-status-badge"
                          style={{ color: statusColor, background: statusBg }}
                        >
                          {statusLabel}
                        </span>
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        ) : (
          <div className="staff-section-empty">
            Building TFI data — this section will populate as TFI scores accumulate.
          </div>
        )}
      </div>
    </div>
  );
}
